import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";

/* Morphing Gradient Orb */
export const MorphingOrb = ({ color1, color2, size = 500, duration = 20, style = {} }) => {
  return (
    <>
      <style>{`
        @keyframes morph-shape {
          0% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; transform: scale(1) rotate(0deg); }
          25% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; transform: scale(1.05) rotate(90deg); }
          50% { border-radius: 50% 60% 30% 60% / 30% 60% 70% 40%; transform: scale(0.95) rotate(180deg); }
          75% { border-radius: 60% 40% 60% 30% / 70% 30% 60% 40%; transform: scale(1.03) rotate(270deg); }
          100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; transform: scale(1) rotate(360deg); }
        }
        @keyframes float-orb {
          0%, 100% { transform: translateY(0) translateX(0); }
          33% { transform: translateY(-25px) translateX(15px); }
          66% { transform: translateY(15px) translateX(-10px); }
        }
      `}</style>
      <div
        className="absolute pointer-events-none"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          background: `radial-gradient(circle, ${color1}, ${color2})`,
          filter: "blur(60px)",
          animation: `morph-shape ${duration}s ease-in-out infinite, float-orb ${duration * 0.7}s ease-in-out infinite`,
          ...style,
        }}
      />
    </>
  );
};

/* Magnetic Button */
export const MagneticButton = ({ children, strength = 0.3, className = "", ...props }) => {
  const buttonRef = useRef(null);

  const handleMouseMove = (e) => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = (e.clientX - centerX) * strength;
    const deltaY = (e.clientY - centerY) * strength;
    btn.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
  };

  const handleMouseLeave = () => {
    if (buttonRef.current) {
      buttonRef.current.style.transform = "translate(0, 0)";
    }
  };

  return (
    <div
      ref={buttonRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`transition-transform duration-200 ease-out inline-block ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

/* Text Reveal Animation */
export const TextReveal = ({ children, delay = 0 }) => {
  const text = typeof children === "string" ? children : "";
  const words = text.split(" ");

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: delay },
    },
  };

  const child = {
    hidden: { opacity: 0, y: 30, filter: "blur(4px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { type: "spring", damping: 15, stiffness: 100 },
    },
  };

  return (
    <motion.span
      variants={container}
      initial="hidden"
      animate="visible"
      aria-label={text}
    >
      {words.map((word, i) => (
        <motion.span
          key={i}
          variants={child}
          className="inline-block"
        >
          {word}{i < words.length - 1 ? "\u00A0" : ""}
        </motion.span>
      ))}
    </motion.span>
  );
};

/* Parallax Section */
export const ParallaxSection = ({ children, speed = 0.5, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", `${speed * 100}%`]);

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div style={{ y }}>{children}</motion.div>
    </div>
  );
};

/* Scroll Progress Indicator */
export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();

  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-teal-500 to-teal-400 origin-left z-[60]"
      style={{ scaleX: scrollYProgress }}
    />
  );
};

/* Mouse Gradient Trail (hero only) */
export const MouseGradient = () => {
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handler = (e) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", handler, { passive: true });
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  return (
    <div
      className="pointer-events-none fixed inset-0 z-30 transition-opacity duration-300 hidden lg:block"
      style={{
        background: `radial-gradient(600px at ${pos.x}px ${pos.y}px, rgba(22, 160, 133, 0.06), transparent 80%)`,
      }}
    />
  );
};

/* Number Ticker */
export const NumberTicker = ({ value, duration = 2000, prefix = "", suffix = "", decimals = 0 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const startTimeRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!isInView) return;

    const animate = (currentTime) => {
      if (!startTimeRef.current) startTimeRef.current = currentTime;
      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * value;
      setDisplayValue(current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isInView, value, duration]);

  const formatNumber = (num) => {
    const fixed = num.toFixed(decimals);
    const parts = fixed.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  return (
    <span ref={ref} className="tabular-nums font-mono">
      {prefix}{formatNumber(displayValue)}{suffix}
    </span>
  );
};

/* Ripple Button */
export const RippleButton = ({ children, onClick, className = "", ...props }) => {
  const [ripples, setRipples] = useState([]);

  const addRipple = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    const newRipple = { x, y, size, key: Date.now() };
    setRipples((prev) => [...prev, newRipple]);
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.key !== newRipple.key));
    }, 600);
  };

  const handleClick = (e) => {
    addRipple(e);
    if (onClick) onClick(e);
  };

  return (
    <button onClick={handleClick} className={`relative overflow-hidden ${className}`} {...props}>
      {children}
      {ripples.map((r) => (
        <span
          key={r.key}
          className="absolute bg-white/20 rounded-full animate-ripple pointer-events-none"
          style={{ left: r.x, top: r.y, width: r.size, height: r.size }}
        />
      ))}
    </button>
  );
};

/* Floating Label Input */
export const FloatingLabelInput = ({ label, type = "text", name, value, onChange, required, ...props }) => {
  const [focused, setFocused] = useState(false);
  const hasValue = value && value.length > 0;

  return (
    <div className="relative">
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="w-full px-4 pt-6 pb-2 rounded-lg bg-white/5 border border-white/10 text-white font-mono text-sm focus:outline-none focus:border-teal-primary/50 transition-all duration-300 peer"
        placeholder=" "
        {...props}
      />
      <label
        className={`absolute left-4 transition-all duration-200 pointer-events-none ${focused || hasValue
          ? "top-2 text-[10px] text-teal-bright"
          : "top-1/2 -translate-y-1/2 text-sm text-slate-600"
          }`}
      >
        {label}
      </label>
    </div>
  );
};

/* Stacking Card */
export const StackingCard = ({ children, index, total }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "start start"],
  });

  const scale = useTransform(scrollYProgress, [0, 1], [0.9, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [100, 0]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0.5, 1]);

  return (
    <motion.div
      ref={ref}
      style={{ scale, y, opacity, position: "sticky", top: `${80 + index * 20}px`, zIndex: total - index }}
    >
      {children}
    </motion.div>
  );
};

/* Magnetic Spotlight / Cursor-Tracking Glowing Button */
export const MagneticSpotlightButton = ({ children, className = "", onClick, as: Component = "button", strength = 0.2, ...props }) => {
  const boundingRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!boundingRef.current) return;
    const btn = boundingRef.current;

    // Magnetic pull logic
    const rect = btn.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = (e.clientX - centerX) * strength;
    const deltaY = (e.clientY - centerY) * strength;
    btn.style.transform = `translate(${deltaX}px, ${deltaY}px)`;

    // Spotlight glow logic
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    btn.style.setProperty("--mouse-x", `${x}px`);
    btn.style.setProperty("--mouse-y", `${y}px`);
  };

  const handleMouseLeave = () => {
    if (!boundingRef.current) return;
    const btn = boundingRef.current;

    // Reset magnetic pull
    btn.style.transform = "translate(0px, 0px)";

    // Reset spotlight glow
    btn.style.removeProperty("--mouse-x");
    btn.style.removeProperty("--mouse-y");
  };

  return (
    <Component
      onClick={onClick}
      ref={boundingRef}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      className={`relative inline-flex items-center justify-center overflow-hidden rounded-lg bg-teal-primary font-bold text-white transition-all duration-200 ease-out hover:bg-teal-dark group ${className}`}
      {...props}
    >
      {/* Glowing element that perfectly follows the mouse cursor */}
      <div className="pointer-events-none absolute -inset-px opacity-0 transition duration-300 group-hover:opacity-100 button-spotlight-glow" />

      {/* Subtle Border Glow */}
      <div className="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-inset ring-white/10 group-hover:ring-white/30 transition-all duration-300" />

      {/* Content z-index to stay above the glow */}
      <div className="relative z-10 flex items-center gap-2">
        {children}
      </div>

      <style>{`
        .button-spotlight-glow {
          opacity: 0;
          background: radial-gradient(
            120px circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
            rgba(255, 255, 255, 0.4),
            transparent 40%
          );
        }
        button:hover .button-spotlight-glow,
        a:hover .button-spotlight-glow {
          opacity: 1;
        }
      `}</style>
    </Component>
  );
};
